import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import { Alert, Badge, Field, LoadingBlock, Muted, Select, Stack } from '../../components/ui';
import { useGetMyLocationsV2LeanQuery } from '../../services/locationServices/__generated__/queries.generated';
import {
  useCreateWorkerDraftMutation,
  useMinimumWageForQuery,
  useMyServiceProviderQuery,
  usePublishWorkerMutation,
  useUpdateWorkerMutation,
  useWorkerPublishChecklistQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { CredentialsSection } from './CredentialsSection';
import { EquipmentPhotosEditor } from './EquipmentPhotosEditor';
import { OfferingsEditor } from './OfferingsEditor';
import { belowWageCount, SERVICE_TYPES, warningText } from './priceTables';
import { ProviderProfileCard } from './ProviderProfileCard';
import { PublishChecklist } from './PublishChecklist';
import { serviceErrorMessage, workerPublishProblemsOf } from './serviceErrors';
import {
  PROVIDER_LOCATIONS_PATH,
  PROVIDER_WORKERS_PATH,
  workerEditPath,
  workerManagePath,
} from './servicePaths';
import { useOfferingsEditor, useWorkerEditor } from './useWorkerEditor';
import { WorkerSectionFields } from './WorkerSections';
import {
  emptyWorkerForm,
  nextWizardStep,
  previousWizardStep,
  resumeWizardStep,
  SECTION_FIELDS,
  toWorkerSettingsInput,
  WIZARD_STEPS,
  wizardStepIndex,
  type WizardStepId,
  type WorkerForm,
  type WorkerFormErrors,
} from './workerForm';

const Shell = styled.div`
  ${tw`flex flex-col`}
  min-height: calc(100vh - 80px);
  background: var(--color-bg-elevated, #fff);
`;

const TopBar = styled.div`
  ${tw`flex items-center justify-between px-4 py-3 sm:px-8`}
  border-bottom: 1px solid var(--color-border);
`;

const Body = styled.main`
  ${tw`flex-1 w-full px-4 pt-8 pb-32 mx-auto sm:px-6`}
  max-width: 760px;
`;

const StepTitle = styled.h1`
  ${tw`text-2xl font-semibold sm:text-3xl`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
  letter-spacing: -0.01em;
`;

const StepDescription = styled.p`
  ${tw`mt-2 mb-8 text-base`}
  color: var(--color-text-secondary);
`;

const Footer = styled.footer`
  ${tw`fixed bottom-0 left-0 right-0 z-10`}
  background: var(--color-bg-elevated, #fff);
`;

const Progress = styled.div<{ $value: number }>`
  height: 6px;
  background: var(--color-bg-secondary);
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $value }) => `${Math.round($value * 100)}%`};
    background: var(--color-text-primary);
    transition: width 0.3s ease;
  }
`;

const FooterRow = styled.div`
  ${tw`flex items-center justify-between px-4 py-4 mx-auto sm:px-8`}
  max-width: 1120px;
`;

const Gap = styled.div`
  ${tw`mb-6`}
`;

/** Step 1 before the worker exists: its location and name, then `createWorkerDraft`. */
function NewWorkerStep({
  form,
  errors,
  onChange,
  locationId,
  onLocation,
}: {
  form: WorkerForm;
  errors: WorkerFormErrors;
  onChange: (patch: Partial<WorkerForm>) => void;
  locationId: string;
  onLocation: (id: string) => void;
}) {
  const locationsQuery = useGetMyLocationsV2LeanQuery({
    variables: { input: { page: 1, limit: 50 } },
    fetchPolicy: 'cache-and-network',
  });
  const locations = locationsQuery.data?.getMyLocationsV2.items ?? [];
  if (locationsQuery.loading && !locationsQuery.data)
    return <LoadingBlock label="Loading your locations" />;
  if (!locations.length) {
    return (
      <Alert tone="info" title="Add a location first">
        Each worker belongs to a location with its own hours.{' '}
        <Link to={PROVIDER_LOCATIONS_PATH}>Add a location</Link>
      </Alert>
    );
  }
  return (
    <Stack $gap={6}>
      <Field
        label="Location"
        htmlFor="worker-location"
        required
        hint="Customers see only its city and ZIP code."
      >
        <Select
          id="worker-location"
          value={locationId}
          onChange={event => onLocation(event.target.value)}
          data-testid="worker-location"
        >
          <option value="">Pick a location</option>
          {locations.map(location => (
            <option key={location.id} value={String(location.id)}>
              {location.name} ({location.city} {location.zipCode})
            </option>
          ))}
        </Select>
      </Field>
      <WorkerSectionFields section="profile" form={form} errors={errors} onChange={onChange} />
    </Stack>
  );
}

/**
 * The worker wizard (spec 019 W6): 7 steps, one screen each. Step 1 creates
 * the draft under a location; from then on every Next saves that step
 * (`updateWorker` with `wizardStep`, or `upsertServiceOffering` for prices),
 * so Save & exit and a later visit resume where the Provider stopped.
 */
export default function WorkerWizardPage() {
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editor = useWorkerEditor(id);
  const { worker, form, errors, change, save, saving } = editor;
  const offerings = useOfferingsEditor(worker);
  const providerQuery = useMyServiceProviderQuery({ fetchPolicy: 'cache-and-network' });
  const provider = providerQuery.data?.myServiceProvider ?? null;

  const [draftForm, setDraftForm] = useState<WorkerForm>(emptyWorkerForm);
  const [draftErrors, setDraftErrors] = useState<WorkerFormErrors>({});
  const [locationId, setLocationId] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);
  const [createDraft, { loading: creating }] = useCreateWorkerDraftMutation();
  const [updateWorker] = useUpdateWorkerMutation();
  const [publishWorker, { loading: publishing }] = usePublishWorkerMutation();

  const requested = searchParams.get('step');
  const step: WizardStepId = !id
    ? 'profile'
    : requested && wizardStepIndex(requested) >= 0
      ? (requested as WizardStepId)
      : resumeWizardStep(worker?.settings.wizardStep);
  const stepDef = WIZARD_STEPS[wizardStepIndex(step)];
  const isLast = step === 'booking';

  const checklistQuery = useWorkerPublishChecklistQuery({
    variables: { workerCarId: id ?? '' },
    skip: !id || !isLast,
    fetchPolicy: 'network-only',
  });
  const locationNumber = worker?.car.location?.id;
  const wageQuery = useMinimumWageForQuery({
    variables: { locationId: Number(locationNumber ?? 0) },
    skip: !locationNumber,
    fetchPolicy: 'cache-first',
  });
  const minimumWage = wageQuery.data?.minimumWageFor.hourlyRate ?? null;

  const goTo = (target: WizardStepId) => {
    setStepError(null);
    if (id) setSearchParams({ step: target });
    window.scrollTo?.(0, 0);
  };

  const failed = (message?: string): false => {
    if (message) setStepError(message);
    return false;
  };

  /** Creates the draft from step 1 and opens step 2. */
  const createWorker = async () => {
    const { input, errors: found } = toWorkerSettingsInput(draftForm, SECTION_FIELDS.profile);
    if (!locationId) {
      setStepError('Pick the location this worker works from');
      return;
    }
    if (Object.keys(found).length) {
      setDraftErrors(found);
      return;
    }
    try {
      const { data } = await createDraft({
        variables: { locationId: Number(locationId), displayName: input.displayName ?? '' },
      });
      const created = data?.createWorkerDraft;
      if (!created) return;
      await updateWorker({
        variables: { workerCarId: created.carId, input: { ...input, wizardStep: 'services' } },
      });
      navigate(workerEditPath(created.carId, 'services'), { replace: true });
    } catch (error) {
      setStepError(serviceErrorMessage(error, 'Could not create the worker'));
    }
  };

  /** Saves the current step; true when the Provider can move on. */
  const saveStep = async (reached: WizardStepId | null): Promise<boolean> => {
    setStepError(null);
    const wizardStep = reached ?? undefined;
    if (step === 'services') {
      if (!SERVICE_TYPES.some(type => offerings.forms[type].enabled)) {
        return failed('Pick at least one service');
      }
      const result = await offerings.saveAll();
      if (!result.ok) return failed(result.message);
      const recorded = await save([], { wizardStep });
      return recorded.ok || failed(recorded.message);
    }
    const keys = stepDef.section ? SECTION_FIELDS[stepDef.section] : [];
    const result = await save(keys, { wizardStep });
    return result.ok || failed(result.message);
  };

  const onNext = async () => {
    const next = nextWizardStep(step);
    if (await saveStep(next)) {
      if (next) goTo(next);
    }
  };

  const onSaveAndExit = async () => {
    if (!id) {
      navigate(PROVIDER_WORKERS_PATH);
      return;
    }
    if (await saveStep(step)) navigate(PROVIDER_WORKERS_PATH);
  };

  const onPublish = async () => {
    if (!id) return;
    if (!(await saveStep(step))) return;
    try {
      await publishWorker({ variables: { workerCarId: id } });
      toast.success('Your worker is listed');
      navigate(workerManagePath(id));
    } catch (error) {
      if (workerPublishProblemsOf(error).length) checklistQuery.refetch();
      setStepError(serviceErrorMessage(error, 'Could not publish'));
    }
  };

  if (id && editor.loading) {
    return (
      <Shell>
        <Body>
          <LoadingBlock label="Loading the worker" />
        </Body>
      </Shell>
    );
  }
  if (id && editor.loadError && !worker) {
    return (
      <Shell>
        <Body>
          <Alert tone="error" title="Worker not found">
            {serviceErrorMessage(editor.loadError, 'This worker could not be loaded.')}
          </Alert>
        </Body>
      </Shell>
    );
  }

  const progress = (wizardStepIndex(step) + 1) / WIZARD_STEPS.length;
  const locationLabel = worker?.car.location
    ? `${worker.car.location.city} ${worker.car.location.zipCode}`
    : null;
  const forms = SERVICE_TYPES.map(type => offerings.forms[type]);
  const lowPriceCount = belowWageCount(forms, minimumWage);
  const needsProfile = !id && !providerQuery.loading && !provider;
  const checklist = checklistQuery.data?.workerPublishChecklist ?? null;

  return (
    <Shell data-testid="worker-wizard">
      <TopBar>
        <Badge tone="neutral" size="sm" data-testid="worker-wizard-step">
          Step {wizardStepIndex(step) + 1} of {WIZARD_STEPS.length}
        </Badge>
        <Button
          type="button"
          theme="secondary"
          size="sm"
          text="Save & exit"
          onClick={onSaveAndExit}
          isLoading={saving}
          dataTestId="worker-wizard-exit"
        />
      </TopBar>
      <Body>
        <StepTitle>{stepDef.title}</StepTitle>
        <StepDescription>{stepDef.description}</StepDescription>
        {stepError && (
          <Gap>
            <Alert tone="error" title="Not saved">
              {stepError}
            </Alert>
          </Gap>
        )}
        {!id && needsProfile && (
          <Gap>
            <ProviderProfileCard />
          </Gap>
        )}
        {!id && !needsProfile && (
          <NewWorkerStep
            form={draftForm}
            errors={draftErrors}
            onChange={patch => {
              setDraftForm(previous => ({ ...previous, ...patch }));
              setDraftErrors({});
            }}
            locationId={locationId}
            onLocation={setLocationId}
          />
        )}
        {id && worker && (
          <>
            {step === 'services' && (
              <Stack $gap={6}>
                {lowPriceCount > 0 && (
                  <Alert tone="info" title="Some prices are low" data-testid="wage-summary">
                    {lowPriceCount === 1 ? '1 price works' : `${lowPriceCount} prices work`} out
                    below the local minimum wage. This is a warning only.
                  </Alert>
                )}
                {offerings.message && (
                  <Alert tone="error" title="Prices not saved">
                    {offerings.message}
                  </Alert>
                )}
                <OfferingsEditor
                  forms={offerings.forms}
                  errors={offerings.errors}
                  onChange={offerings.change}
                  minimumWage={minimumWage}
                  serverWarnings={worker.priceWarnings}
                  handoverAllowed={worker.worker.backgroundCheckVerified}
                />
              </Stack>
            )}
            {step === 'equipment' && (
              <Stack $gap={8}>
                <EquipmentPhotosEditor worker={worker} />
                <WorkerSectionFields
                  section="supplies"
                  form={form}
                  errors={errors}
                  onChange={change}
                />
              </Stack>
            )}
            {step === 'credentials' && <CredentialsSection worker={worker} />}
            {stepDef.section && step !== 'equipment' && (
              <WorkerSectionFields
                section={stepDef.section}
                form={form}
                errors={errors}
                onChange={change}
                locationLabel={locationLabel}
              />
            )}
            {isLast && (
              <Gap style={{ marginTop: 32 }}>
                <PublishChecklist
                  problems={checklist}
                  warnings={worker.priceWarnings.map(warningText)}
                  onGoTo={goTo}
                />
              </Gap>
            )}
          </>
        )}
        {!id && !needsProfile && <Muted $size="sm">You can change all of this later.</Muted>}
      </Body>
      <Footer>
        <Progress $value={progress} aria-hidden />
        <FooterRow>
          {id && previousWizardStep(step) ? (
            <Button
              type="button"
              theme="text"
              text="Back"
              onClick={() => goTo(previousWizardStep(step)!)}
            />
          ) : (
            <span />
          )}
          {!id ? (
            <Button
              type="button"
              text="Create worker"
              onClick={createWorker}
              isLoading={creating}
              isDisabled={needsProfile}
              dataTestId="worker-wizard-create"
            />
          ) : isLast ? (
            <Button
              type="button"
              text="Publish"
              onClick={onPublish}
              isLoading={publishing || saving}
              isDisabled={!checklist || checklist.length > 0}
              dataTestId="worker-wizard-publish"
            />
          ) : (
            <Button
              type="button"
              text="Next"
              onClick={onNext}
              isLoading={saving || offerings.saving}
              dataTestId="worker-wizard-next"
            />
          )}
        </FooterRow>
      </Footer>
    </Shell>
  );
}
